import { useState, useEffect, useRef } from 'react';

export default function StickyCourseCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  // Keep --course-sticky-h CSS variable updated so other elements can offset above this bar
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const update = () => {
      const h = bar.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--course-sticky-h', `${h}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(bar);
    return () => {
      ro.disconnect();
      // Reset the variable when this component unmounts
      document.documentElement.style.setProperty('--course-sticky-h', '0px');
    };
  }, []);

  // Show the bar once the user has scrolled past course-structure
  useEffect(() => {
    const trigger = document.getElementById('course-structure');
    if (!trigger) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When course-structure leaves the viewport (scrolled past), reveal the bar
        setIsVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(trigger);
    return () => observer.disconnect();
  }, []);

  const goToPricing = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/');
      // Give the router a moment to settle, then scroll to the contact section
      setTimeout(() => {
        const el = document.getElementById('contact-section');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        transition: isVisible
          ? 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.45s cubic-bezier(0.22, 1, 0.36, 1)'
          : 'transform 0.38s cubic-bezier(0.55, 0, 1, 0.45), opacity 0.3s ease-in',
        transform: isVisible ? 'translateY(0)' : 'translateY(110%)',
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
      }}
    >
      {/* Subtle fade above the bar so it doesn't hard-cut over page content */}
      <div
        className="h-6 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, rgba(26,8,0,0.12))' }}
      />

      <div
        ref={barRef}
        className="bg-[#1A0800] border-t border-[#BA3D03]/40 shadow-2xl relative overflow-hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Subtle warm shimmer */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#BA3D03]/5 via-transparent to-[#E48523]/5 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-12 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Left: label */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
              <i className="ri-award-line text-[#E48523] text-lg"></i>
            </div>
            <p className="text-white text-sm font-medium leading-snug">
              Interested in this course for your team?
              <span className="hidden sm:inline text-[#E8C580]/70 font-normal ml-1">
                Request company pricing — we respond within 1 business day.
              </span>
            </p>
          </div>

          {/* Right: CTA button */}
          <button
            onClick={goToPricing}
            className="w-full sm:w-auto flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#BA3D03] text-white rounded-lg text-sm font-bold hover:bg-[#9A3203] active:bg-[#7A2803] transition-all whitespace-nowrap cursor-pointer shadow-md"
          >
            <i className="ri-building-line text-base w-4 h-4 flex items-center justify-center"></i>
            Request Company Pricing
          </button>
        </div>
      </div>
    </div>
  );
}
